/**
 * aggReportItemRenderer.ts
 *
 * Renders Stats > Aggregate report items (frequency table, cross-tabulation, bar/line graph) to
 * self-contained HTML strings for display in a WebView — the results-card equivalent of
 * chartRenderer.ts, and a direct sibling to it (reuses its renderChart for Bar/Line). Same
 * conventions as chartRenderer.ts: pure functions, no external dependencies, never throws (a
 * missing/malformed input produces a visible inline placeholder or error block instead of
 * blanking the whole results card), inline-styled output with no external CSS/script
 * dependency.
 */

import { renderChart, type ChartConfig, type ChartSeries } from './chartRenderer';
import type { AdminAssessmentDto } from '../types/adminAssessment';
import type { StatsAggDataPointDto, StatsAggItemResultDto, StatsAggReportItemDefDto } from '../types/api/stats';

const FONT = 'Arial, sans-serif';

/** Space (px) between a Bar/Line item's title and the start of its graph. */
const CHART_TITLE_GAP = 16;

const escapeHtml = (s: string): string =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const itemTitleHtml = (title?: string): string =>
  title
    ? `<h3 style="margin:0 0 8px;font-size:14px;font-weight:normal;color:#2c3e50;font-family:${FONT};text-align:center;">${escapeHtml(title)}</h3>`
    : '';

const itemErrorHtml = (message: string): string =>
  `<div style="text-align:center;margin:12px 0;"><p style="color:#e74c3c;font-family:${FONT};font-size:13px;border:1px solid #e74c3c;border-radius:6px;padding:10px;display:inline-block;">Report item error: ${escapeHtml(message)}</p></div>`;

const itemEmptyHtml = (): string =>
  `<div style="text-align:center;margin:12px 0;"><p style="color:#7f8c8d;font-family:${FONT};font-size:13px;">No data.</p></div>`;

/** Preserves first-seen order rather than sorting, so category order matches the order the
 * backend returned data points in (typically the order set by whatever produced the aggregate). */
const uniqueInOrder = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    if (!seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  });
  return result;
};

/** Data-value → display-label maps, keyed by `${assessmentId}|${mVar}` (see buildAggDataLabelLookup). */
export type AggDataLabelLookup = Record<string, Record<string, string>>;

const dataLabelKey = (assessmentId: string, mVar: string): string => `${assessmentId}|${mVar}`;

/**
 * Builds the lookup of data labels from assessment definitions (StatsAggVarDto.dataLabels), keyed
 * by assessment sourceId + variable (mVar). Variables with no labels are omitted.
 */
/** Variable display names (StatsAggVarDto.dName), keyed by `${assessmentId}|${mVar}`. */
export type AggVarNameLookup = Record<string, string>;

export function buildAggVarNameLookup(assessments: AdminAssessmentDto[]): AggVarNameLookup {
  const lookup: AggVarNameLookup = {};
  assessments.forEach((assessment) => {
    (assessment.aggVars ?? []).forEach((aggVar) => {
      if (aggVar.dName) {
        lookup[dataLabelKey(assessment.sourceId, aggVar.mVar)] = aggVar.dName;
      }
    });
  });
  return lookup;
}

export function buildAggDataLabelLookup(assessments: AdminAssessmentDto[]): AggDataLabelLookup {
  const lookup: AggDataLabelLookup = {};
  assessments.forEach((assessment) => {
    (assessment.aggVars ?? []).forEach((aggVar) => {
      const labels = aggVar.dataLabels ?? [];
      if (labels.length === 0) {
        return;
      }
      const map: Record<string, string> = {};
      labels.forEach((entry) => {
        if (entry.dataLabel) {
          map[entry.dataValue.trim()] = entry.dataLabel;
        }
      });
      lookup[dataLabelKey(assessment.sourceId, aggVar.mVar)] = map;
    });
  });
  return lookup;
}

/**
 * Drops data points whose category is empty (a null answer) — they'd show as a blank row/column.
 * Label is the item's VarX value; GroupLabel its VarY value, which is only checked when the item
 * actually has a VarY (otherwise it's legitimately '').
 */
function dropEmptyValues(itemDef: StatsAggReportItemDefDto, data: StatsAggDataPointDto[]): StatsAggDataPointDto[] {
  const hasGroup = !!(itemDef.VarY ?? '').trim();
  return data.filter(
    (point) => (point.Label ?? '').trim() !== '' && (!hasGroup || (point.GroupLabel ?? '').trim() !== '')
  );
}

/**
 * Replaces raw data values with their data labels where defined: Label holds the item's VarX
 * value and GroupLabel its VarY value. Values with no label are left as they are.
 */
function applyDataLabels(
  itemDef: StatsAggReportItemDefDto,
  data: StatsAggDataPointDto[],
  lookup: AggDataLabelLookup | undefined
): StatsAggDataPointDto[] {
  if (!lookup) {
    return data;
  }
  const xLabels = lookup[dataLabelKey(itemDef.AssessmentId, itemDef.VarX)];
  const yLabels = lookup[dataLabelKey(itemDef.AssessmentId, itemDef.VarY)];
  if (!xLabels && !yLabels) {
    return data;
  }
  return data.map((point) => ({
    ...point,
    Label: xLabels?.[(point.Label ?? '').trim()] ?? point.Label,
    GroupLabel: yLabels?.[(point.GroupLabel ?? '').trim()] ?? point.GroupLabel,
  }));
}

export interface FrequencyTableConfig {
  title?: string;
  rows: { label: string; value: string }[];
}

const toCount = (value: string): number => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Percent of total to one decimal place, e.g. "45.5%". Blank when there is no total to divide by. */
const formatPercent = (count: number, total: number): string =>
  total > 0 ? `${((count / total) * 100).toFixed(1)}%` : '';

/** Frequency table — one row per category/value with its count and percent of the total, plus a
 * Total row (indented label) at the bottom. */
export function renderFrequencyTable(config: FrequencyTableConfig): string {
  const total = config.rows.reduce((sum, row) => sum + toCount(row.value), 0);
  const cell = 'padding:6px 12px;border-bottom:1px solid #e5ebf3;font-family:' + FONT + ';font-size:13px;';

  const rowsHtml = config.rows
    .map(
      (row) => `<tr>
        <td style="${cell}color:#334155;">${escapeHtml(row.label)}</td>
        <td style="${cell}color:#0f172a;text-align:right;">${escapeHtml(row.value)}</td>
        <td style="${cell}color:#0f172a;text-align:right;">${escapeHtml(formatPercent(toCount(row.value), total))}</td>
      </tr>`
    )
    .join('');

  const totalCell = `padding:6px 12px;border-top:2px solid #cbd5e1;font-family:${FONT};font-size:13px;`;
  const totalHtml = `<tr>
        <td style="${totalCell}color:#334155;padding-left:28px;">Total</td>
        <td style="${totalCell}color:#0f172a;text-align:right;">${escapeHtml(String(Math.round(total * 100) / 100))}</td>
        <td style="${totalCell}color:#0f172a;text-align:right;">${escapeHtml(formatPercent(total, total))}</td>
      </tr>`;

  return `<div style="margin:12px 0;">
${itemTitleHtml(config.title)}
<table style="width:100%;max-width:420px;margin:0 auto;border-collapse:collapse;">${rowsHtml}${totalHtml}</table>
</div>`;
}

/** Item Params (StatsAggReportItemDefDto.Params) understood by the CrossTab renderer; Value '1' = on. */
export const AGG_PARAM_SHOW_ROW_PERCENT = 'showRowPercent';
export const AGG_PARAM_SHOW_COLUMN_PERCENT = 'showColumnPercent';
export const AGG_PARAM_SHOW_VARIABLE_NAMES = 'showVariableNames';

export const getAggItemParamFlag = (itemDef: StatsAggReportItemDefDto, name: string): boolean =>
  (itemDef.Params ?? []).some((param) => param.Name === name && param.Value === '1');

export interface CrossTabConfig {
  title?: string;
  rowLabels: string[];
  colLabels: string[];
  /** matrix[r][c] — a cell value, keyed by rowLabels[r]/colLabels[c]. */
  matrix: string[][];
  /** Table cells show their percent of the row total. Mutually exclusive with showColumnPercent
   * (row wins if both are set). Neither = counts only. */
  showRowPercent?: boolean;
  /** Table cells show their percent of the column total. */
  showColumnPercent?: boolean;
  /** When set, the row variable's name runs vertically down the left of the table and the column
   * variable's name sits above it, under the title. */
  rowVariableName?: string;
  columnVariableName?: string;
}

/** HTML table with a blank corner cell, one column header per colLabels entry, and one row
 * header per rowLabels entry. */
export function renderCrossTab(config: CrossTabConfig): string {
  const fmt = (n: number): string => String(Math.round(n * 100) / 100);
  const numAt = (r: number, c: number): number => toCount(config.matrix[r]?.[c] ?? '');

  const rowTotals = config.rowLabels.map((_, r) => config.colLabels.reduce((sum, __, c) => sum + numAt(r, c), 0));
  const colTotals = config.colLabels.map((_, c) => config.rowLabels.reduce((sum, __, r) => sum + numAt(r, c), 0));
  // Rows and columns sum to the same figure, so one grand total serves the lower-right corner.
  const grandTotal = rowTotals.reduce((sum, n) => sum + n, 0);

  const showPercent = !!(config.showRowPercent || config.showColumnPercent);

  const BORDER_HEAD = 'border-bottom:2px solid #cbd5e1;';
  const BORDER_BODY = 'border-bottom:1px solid #e5ebf3;';
  const BORDER_TOTAL = 'border-top:2px solid #cbd5e1;';
  const baseCell = `font-family:${FONT};font-size:13px;`;

  // Each data column is a count cell (right-justified) plus, when a percent is shown, a "(97.2%)"
  // cell (left-justified, smaller and lighter), so counts and percents each line up down a column.
  const dataCells = (count: string, percent: string, border: string): string => {
    const countCell = `<td style="padding:6px ${showPercent ? '2px' : '12px'} 6px 12px;${border}color:#0f172a;${baseCell}text-align:right;">${escapeHtml(count)}</td>`;
    if (!showPercent) {
      return countCell;
    }
    const percentCell = `<td style="padding:6px 12px 6px 2px;${border}color:#64748b;font-family:${FONT};font-size:11px;text-align:left;">${percent ? escapeHtml(`(${percent})`) : ''}</td>`;
    return countCell + percentCell;
  };

  const headerCell = (text: string) =>
    `<th${showPercent ? ' colspan="2"' : ''} style="padding:6px 12px;${BORDER_HEAD}color:#334155;${baseCell}font-weight:normal;text-align:right;">${escapeHtml(text)}</th>`;

  // Table cells show the percent chosen in the item setup (of the row or column total). The Total
  // column / Total row show each row's / column's share of the grand total, and the corner 100.0%,
  // but only when a percent is being shown at all.
  const bodyPercent = (count: number, rowTotal: number, colTotal: number): string => {
    if (config.showRowPercent) {
      return formatPercent(count, rowTotal);
    }
    if (config.showColumnPercent) {
      return formatPercent(count, colTotal);
    }
    return '';
  };
  const totalPercent = (count: number): string => (showPercent ? formatPercent(count, grandTotal) : '');

  const headerCells = config.colLabels.map(headerCell).join('') + headerCell('Total');

  // Variable names (optional): the row variable runs vertically (bottom-to-top) down the left edge,
  // spanning every body row and the Total row; the column variable is a centered line above the
  // column headers. Both only exist when names were requested.
  const showNames = !!(config.rowVariableName || config.columnVariableName);
  const nameStyle = `color:#334155;${baseCell}`;
  const rowNameCell = showNames
    ? `<td rowspan="${config.rowLabels.length + 1}" style="padding:6px 4px;${nameStyle}text-align:center;vertical-align:middle;writing-mode:vertical-rl;transform:rotate(180deg);">${escapeHtml(config.rowVariableName ?? '')}</td>`
    : '';
  const leftSpacer = showNames ? `<th style="padding:0;"></th>` : '';
  const dataColumnSpan = (config.colLabels.length + 1) * (showPercent ? 2 : 1);
  const columnNameRow = showNames
    ? `<tr>${leftSpacer}<th></th><th colspan="${dataColumnSpan}" style="padding:6px 12px;${nameStyle}font-weight:normal;text-align:center;">${escapeHtml(config.columnVariableName ?? '')}</th></tr>`
    : '';

  const bodyRows = config.rowLabels
    .map((row, r) => {
      const cells = config.colLabels
        .map((_, c) => dataCells(config.matrix[r]?.[c] ?? '', bodyPercent(numAt(r, c), rowTotals[r], colTotals[c]), BORDER_BODY))
        .join('');
      return `<tr>
        ${r === 0 ? rowNameCell : ''}<th style="padding:6px 12px;${BORDER_BODY}color:#334155;${baseCell}font-weight:normal;text-align:left;">${escapeHtml(row)}</th>
        ${cells}${dataCells(fmt(rowTotals[r]), totalPercent(rowTotals[r]), BORDER_BODY)}
      </tr>`;
    })
    .join('');

  const totalRow = `<tr>
        <td style="padding:6px 12px 6px 28px;${BORDER_TOTAL}color:#334155;${baseCell}text-align:left;">Total</td>
        ${colTotals.map((n) => dataCells(fmt(n), totalPercent(n), BORDER_TOTAL)).join('')}${dataCells(fmt(grandTotal), totalPercent(grandTotal), BORDER_TOTAL)}
      </tr>`;

  return `<div style="margin:12px 0;overflow-x:auto;">
${itemTitleHtml(config.title)}
<table style="border-collapse:collapse;margin:0 auto;">
${columnNameRow}
<tr>${leftSpacer}<th style="padding:6px 12px;${BORDER_HEAD}"></th>${headerCells}</tr>
${bodyRows}
${totalRow}
</table>
</div>`;
}

/**
 * Builds one report item's rendered HTML block from its definition and matching run result,
 * dispatching to the renderer for its Type. Never throws — a missing result, empty data, or
 * unknown Type all degrade to a visible placeholder/error rather than blanking the whole card.
 */
export function buildAggItemHtml(
  itemDef: StatsAggReportItemDefDto,
  result: StatsAggItemResultDto | undefined,
  dataLabels?: AggDataLabelLookup,
  varNames?: AggVarNameLookup
): string {
  try {
    const data: StatsAggDataPointDto[] = applyDataLabels(itemDef, dropEmptyValues(itemDef, result?.Data ?? []), dataLabels);
    if (data.length === 0) {
      return `${itemTitleHtml(itemDef.DName)}${itemEmptyHtml()}`;
    }

    switch (itemDef.Type) {
      case 'Frequency': {
        return renderFrequencyTable({
          title: itemDef.DName,
          rows: data.map((d) => ({ label: d.Label, value: d.Value })),
        });
      }
      case 'CrossTab': {
        const rowLabels = uniqueInOrder(data.map((d) => d.Label));
        const colLabels = uniqueInOrder(data.map((d) => d.GroupLabel));
        const matrix = rowLabels.map((rowLabel) =>
          colLabels.map((colLabel) => {
            const point = data.find((d) => d.Label === rowLabel && d.GroupLabel === colLabel);
            return point?.Value ?? '';
          })
        );
        return renderCrossTab({
          title: itemDef.DName,
          rowLabels,
          colLabels,
          matrix,
          showRowPercent: getAggItemParamFlag(itemDef, AGG_PARAM_SHOW_ROW_PERCENT),
          showColumnPercent: getAggItemParamFlag(itemDef, AGG_PARAM_SHOW_COLUMN_PERCENT),
          ...(getAggItemParamFlag(itemDef, AGG_PARAM_SHOW_VARIABLE_NAMES)
            ? {
                rowVariableName: varNames?.[dataLabelKey(itemDef.AssessmentId, itemDef.VarX)] || itemDef.VarX,
                columnVariableName: varNames?.[dataLabelKey(itemDef.AssessmentId, itemDef.VarY)] || itemDef.VarY,
              }
            : {}),
        });
      }
      case 'Bar': {
        const labels = uniqueInOrder(data.map((d) => d.Label));
        const values = labels.map((label) => data.find((d) => d.Label === label)?.Value ?? '0');
        const config: ChartConfig = { type: 'bar', title: itemDef.DName, plainTitle: true, titleGap: CHART_TITLE_GAP, labels, values };
        return renderChart(config);
      }
      case 'Line': {
        const labels = uniqueInOrder(data.map((d) => d.Label));
        const groupLabels = uniqueInOrder(data.map((d) => d.GroupLabel));
        // No GroupLabel on any point ⇒ one implicit, unlabeled series (matches Bar's shape).
        const series: ChartSeries[] = (groupLabels.length === 1 && groupLabels[0] === '')
          ? [{ values: labels.map((label) => data.find((d) => d.Label === label)?.Value ?? '0') }]
          : groupLabels.map((group) => ({
              label: group,
              values: labels.map((label) => data.find((d) => d.Label === label && d.GroupLabel === group)?.Value ?? '0'),
            }));
        const config: ChartConfig = { type: 'line', title: itemDef.DName, plainTitle: true, titleGap: CHART_TITLE_GAP, labels, series };
        return renderChart(config);
      }
      default:
        return itemErrorHtml(`unknown report item type "${itemDef.Type}".`);
    }
  } catch (error) {
    return itemErrorHtml(error instanceof Error ? error.message : String(error));
  }
}

const parsePos = (pos: string): number => {
  const parsed = parseInt(pos, 10);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

/**
 * Renders every item in a report, in Pos order, concatenated into one HTML string with a
 * visual spacer between items — ready to pass to wrapReportHtml() for display in a WebView.
 */
export function renderAggReportItems(
  itemDefs: StatsAggReportItemDefDto[],
  results: StatsAggItemResultDto[],
  dataLabels?: AggDataLabelLookup,
  varNames?: AggVarNameLookup
): string {
  const resultByItemId = new Map(results.map((r) => [r.ItemId, r]));
  const sortedItems = [...itemDefs].sort((a, b) => parsePos(a.Pos) - parsePos(b.Pos));

  if (sortedItems.length === 0) {
    return `<div style="text-align:center;margin:24px 0;"><p style="color:#7f8c8d;font-family:${FONT};font-size:13px;">This report has no items configured yet.</p></div>`;
  }

  return sortedItems
    .map((itemDef) => buildAggItemHtml(itemDef, resultByItemId.get(itemDef.ItemId), dataLabels, varNames))
    .join('<div style="height:1px;background:#e5ebf3;margin:16px 0;"></div>');
}
